import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

type Product = {
  id: number
  sku: string
  name: string
  description: string
  product_type: 'PRODUCT' | 'SERVICE' | 'CONSUMABLE'
  sales_price: string
  purchase_cost: string
  default_tax_rate: string
  revenue_account: number | null
  revenue_account_code: string | null
  expense_account: number | null
  expense_account_code: string | null
  stock_asset_account: number | null
  stock_asset_account_code: string | null
  unit_of_measure: string
  is_active: boolean
  total_on_hand: string
}

type StockLevel = {
  id: number
  product: number
  product_sku: string
  product_name: string
  warehouse: number
  warehouse_code: string
  warehouse_name: string
  quantity: string
}

type StockMovement = {
  id: number
  product: number
  product_sku: string
  product_name: string
  warehouse: number
  warehouse_code: string
  destination_warehouse: number | null
  destination_warehouse_code: string | null
  movement_type: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT'
  quantity: string
  occurred_at: string
  reference: string
  notes: string
}

// ─────────────────── Constants ───────────────────
const TYPE_LABEL: Record<Product['product_type'], string> = {
  PRODUCT: 'Storable',
  SERVICE: 'Service',
  CONSUMABLE: 'Consumable',
}

const TYPE_COLOR: Record<Product['product_type'], string> = {
  PRODUCT: 'bg-blue-100 text-blue-800',
  SERVICE: 'bg-purple-100 text-purple-800',
  CONSUMABLE: 'bg-amber-100 text-amber-800',
}

const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const MOVEMENT_COLOR: Record<StockMovement['movement_type'], string> = {
  IN: 'bg-green-100 text-green-800',
  OUT: 'bg-red-100 text-red-800',
  TRANSFER: 'bg-blue-100 text-blue-800',
  ADJUSTMENT: 'bg-amber-100 text-amber-800',
}

type TabKey = 'details' | 'stock' | 'files'

// ─────────────────── Page ───────────────────
export default function InventoryProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const productId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const productQ = useQuery({
    queryKey: ['inventory', 'product', productId],
    queryFn: async () => (await api.get<Product>(`/inventory/products/${productId}/`)).data,
    enabled: !!productId,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Product>) =>
      (await api.patch(`/inventory/products/${productId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'product', productId] })
      qc.invalidateQueries({ queryKey: ['inventory', 'products'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/inventory/products/${productId}/`),
    onSuccess: () => {
      toast.success('Product deleted')
      qc.invalidateQueries({ queryKey: ['inventory', 'products'] })
      navigate('/admin/inventory/products')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (productQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (productQ.isError || !productQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Product not found.</p>
          <Link to="/admin/inventory/products" className="text-blue-600 hover:underline">← Back to products</Link>
        </div>
      </AdminLayout>
    )
  }

  const product = productQ.data
  const accounts = accountsQ.data || []
  const revenueAccounts = accounts.filter((a) => a.is_active && a.account_type === 'REVENUE')
  const expenseAccounts = accounts.filter((a) => a.is_active && (a.account_type === 'EXPENSE' || a.account_type === 'COST_OF_SALES'))
  const assetAccounts = accounts.filter((a) => a.is_active && a.account_type === 'ASSET')
  const showStock = product.product_type !== 'SERVICE'

  const save = (payload: Partial<Product>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/inventory/products" className="text-muted-foreground hover:text-foreground hover:underline">← Products</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{product.sku}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={product.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_COLOR[product.product_type]}`}>
                {TYPE_LABEL[product.product_type]}
              </span>
              <span> · </span>
              <span className="font-mono">{product.unit_of_measure}</span>
              <span> · </span>
              <span className="font-mono">{Number(product.sales_price).toFixed(2)} sales</span>
              {showStock && (
                <>
                  <span> · </span>
                  <span className="font-mono">{Number(product.total_on_hand).toFixed(2)} on hand</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={product.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `product ${product.sku} (${product.name})`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete product
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          <button
            onClick={() => setTab('details')}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === 'details' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Details
          </button>
          {showStock && (
            <button
              onClick={() => setTab('stock')}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === 'stock' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Stock
            </button>
          )}
          <button
            onClick={() => setTab('files')}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === 'files' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Files
          </button>
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          product={product}
          revenueAccounts={revenueAccounts}
          expenseAccounts={expenseAccounts}
          assetAccounts={assetAccounts}
          onSave={save}
        />
      )}
      {tab === 'stock' && showStock && <StockTab productId={product.id} />}
      {tab === 'files' && <FilesTab parent="inventory.product" parentId={productId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  product, revenueAccounts, expenseAccounts, assetAccounts, onSave,
}: {
  product: Product
  revenueAccounts: Account[]
  expenseAccounts: Account[]
  assetAccounts: Account[]
  onSave: (payload: Partial<Product>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Identity">
        <Row label="SKU">
          <InlineText
            value={product.sku}
            onSave={(v) => onSave({ sku: v })}
            className="font-mono"
          />
        </Row>
        <Row label="Name">
          <InlineText value={product.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Type">
          <InlineSelect
            value={product.product_type}
            displayLabel={
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_COLOR[product.product_type]}`}>
                {TYPE_LABEL[product.product_type]}
              </span>
            }
            options={[
              { value: 'PRODUCT', label: 'Storable — tracked stock' },
              { value: 'SERVICE', label: 'Service — no stock' },
              { value: 'CONSUMABLE', label: 'Consumable — tracked, written off' },
            ]}
            onSave={(v) => onSave({ product_type: v as Product['product_type'] })}
            helper="Storable and Consumable products track inventory; Service products don't."
          />
        </Row>
        <Row label="Unit of measure">
          <InlineText
            value={product.unit_of_measure}
            onSave={(v) => onSave({ unit_of_measure: v })}
            placeholder="pcs, kg, hours, …"
            helper="Free-text. Defaults to 'pcs'."
          />
        </Row>
        <Row label="Description">
          <InlineText
            value={product.description}
            multiline
            onSave={(v) => onSave({ description: v })}
            placeholder="Customer-visible description (appears on quotes/invoices)"
          />
        </Row>
      </Section>

      <Section title="Pricing">
        <Row label="Sales price">
          <InlineNumber
            value={product.sales_price}
            onSave={(v) => onSave({ sales_price: v })}
            displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
          />
        </Row>
        <Row label="Purchase cost">
          <InlineNumber
            value={product.purchase_cost}
            onSave={(v) => onSave({ purchase_cost: v })}
            displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
          />
        </Row>
        <Row label="Default tax rate">
          <InlineNumber
            value={product.default_tax_rate}
            step="0.01"
            onSave={(v) => onSave({ default_tax_rate: v })}
            displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
            helper="Used as the default tax rate when this product is added to a quote, order, invoice, or bill."
          />
        </Row>
      </Section>

      <Section title="GL accounts">
        <Row label="Revenue account">
          <InlineSelect
            value={product.revenue_account ? String(product.revenue_account) : ''}
            displayLabel={(() => {
              const a = revenueAccounts.find((x) => x.id === product.revenue_account)
              return a ? `${a.code} ${a.name}` : <Dash />
            })()}
            allowClear
            options={revenueAccounts.map((a) => ({
              value: String(a.id),
              label: `${a.code} ${a.name}`,
              searchText: `${a.code} ${a.name}`,
            }))}
            onSave={(v) => onSave({ revenue_account: v ? Number(v) : null })}
            helper="Credit account on sales (invoices, quotes, orders)."
          />
        </Row>
        <Row label="Expense account">
          <InlineSelect
            value={product.expense_account ? String(product.expense_account) : ''}
            displayLabel={(() => {
              const a = expenseAccounts.find((x) => x.id === product.expense_account)
              return a ? `${a.code} ${a.name}` : <Dash />
            })()}
            allowClear
            options={expenseAccounts.map((a) => ({
              value: String(a.id),
              label: `${a.code} ${a.name}`,
              searchText: `${a.code} ${a.name}`,
            }))}
            onSave={(v) => onSave({ expense_account: v ? Number(v) : null })}
            helper="Debit account on bills / COGS posting."
          />
        </Row>
        <Row label="Stock asset account">
          <InlineSelect
            value={product.stock_asset_account ? String(product.stock_asset_account) : ''}
            displayLabel={(() => {
              const a = assetAccounts.find((x) => x.id === product.stock_asset_account)
              return a ? `${a.code} ${a.name}` : <Dash />
            })()}
            allowClear
            options={assetAccounts.map((a) => ({
              value: String(a.id),
              label: `${a.code} ${a.name}`,
              searchText: `${a.code} ${a.name}`,
            }))}
            onSave={(v) => onSave({ stock_asset_account: v ? Number(v) : null })}
            helper="Balance-sheet account for on-hand inventory value."
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Stock tab ───────────────────
// Read-only. On-hand per warehouse + recent movements. Movements are
// created from sales orders, purchase flows, and manual adjustments — they
// aren't directly edited here.

function StockTab({ productId }: { productId: number }) {
  const levelsQ = useQuery({
    queryKey: ['inventory', 'stock-levels', { product: productId }],
    queryFn: async () => (await api.get<StockLevel[]>(`/inventory/stock-levels/?product=${productId}`)).data,
  })
  const movementsQ = useQuery({
    queryKey: ['inventory', 'movements', { product: productId }],
    queryFn: async () => (await api.get<StockMovement[]>(`/inventory/movements/?product=${productId}`)).data,
  })

  const levels = levelsQ.data || []
  const movements = movementsQ.data || []
  const totalOnHand = levels.reduce((s, l) => s + Number(l.quantity), 0)

  return (
    <div className="space-y-6 max-w-3xl">
      <section className="bg-card rounded-lg shadow p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">On hand by warehouse</h3>
          <span className="text-base font-mono font-bold">{totalOnHand.toFixed(2)} total</span>
        </div>
        {levelsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : levels.length === 0 ? (
          <p className="text-muted-foreground text-sm">No stock anywhere yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Warehouse</Th>
                <Th className="text-right">On hand</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {levels.map((l) => (
                <tr key={l.id}>
                  <Td>
                    <span className="font-mono text-xs">{l.warehouse_code}</span>{' '}
                    <span className="text-muted-foreground">{l.warehouse_name}</span>
                  </Td>
                  <Td className="text-right font-mono">{Number(l.quantity).toFixed(2)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-card rounded-lg shadow p-6">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">Recent movements</h3>
        {movementsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : movements.length === 0 ? (
          <p className="text-muted-foreground text-sm">No stock movements yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>When</Th>
                <Th>Type</Th>
                <Th>Warehouse</Th>
                <Th className="text-right">Quantity</Th>
                <Th>Reference</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {movements.map((m) => (
                <tr key={m.id}>
                  <Td className="text-xs font-mono">{m.occurred_at?.slice(0, 10) || <Dash />}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${MOVEMENT_COLOR[m.movement_type]}`}>
                      {m.movement_type}
                    </span>
                  </Td>
                  <Td className="text-xs">
                    <span className="font-mono">{m.warehouse_code}</span>
                    {m.destination_warehouse_code && (
                      <> → <span className="font-mono">{m.destination_warehouse_code}</span></>
                    )}
                  </Td>
                  <Td className="text-right font-mono">{Number(m.quantity).toFixed(2)}</Td>
                  <Td className="text-xs">{m.reference || <Dash />}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
